/**
 * Google Analytics 4 Event Tracking
 * Tracks key user actions for conversion optimization
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

const GA_ID = 'G-J0MY3RFJG8';

function gtag(...args: any[]) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
}

// ── Page views ──
export function trackPageView(path: string, title?: string) {
  gtag('event', 'page_view', {
    page_location: `https://parcleadpro.com${path}`,
    page_title: title || document.title,
    send_to: GA_ID,
  });
}

// ── Signup funnel ──
export function trackSignupStart(method: 'email' | 'google' | 'magic_link') {
  gtag('event', 'sign_up_start', { method, send_to: GA_ID });
}

export function trackSignupComplete(method: 'email' | 'google' | 'magic_link') {
  gtag('event', 'sign_up', { method, send_to: GA_ID });
}

// ── Search events ──
export function trackSearch(county: string, state: string, resultCount: number) {
  gtag('event', 'parcel_search', {
    county,
    state,
    result_count: resultCount,
    send_to: GA_ID,
  });
}

export function trackSearchLimitReached(plan: string) {
  gtag('event', 'search_limit_reached', { plan, send_to: GA_ID });
}

// ── Upgrade funnel ──
export function trackUpgradeClick(plan: 'starter' | 'investor' | 'pro' | 'enterprise') {
  const prices = { starter: 39, investor: 79, pro: 129, enterprise: 0 };
  gtag('event', 'begin_checkout', {
    items: [{ item_name: plan, price: prices[plan] }],
    send_to: GA_ID,
  });
}

export function trackPurchaseComplete(plan: 'starter' | 'investor' | 'pro' | 'enterprise', price: number) {
  gtag('event', 'purchase', {
    transaction_id: `${Date.now()}_${plan}`,
    value: price,
    currency: 'USD',
    items: [{ item_name: `${plan}_plan`, price }],
    send_to: GA_ID,
  });
}

// ── Exit intent ──
export function trackExitIntent(action: 'shown' | 'dismissed' | 'converted') {
  gtag('event', 'exit_intent', { action, send_to: GA_ID });
}

// ── Free trial CTA clicks ──
export function trackTrialClick(location: 'hero' | 'nav' | 'pricing' | 'exit_intent' | 'cta_section') {
  gtag('event', 'start_trial_click', { location, send_to: GA_ID });
}

// ── Support ──
export function trackTicketSubmit(category: string) {
  gtag('event', 'ticket_submit', { category, send_to: GA_ID });
}

// ── Lead capture ──
export function trackLeadCapture(source: string) {
  gtag('event', 'generate_lead', { source, send_to: GA_ID });
}

// ── Feature engagement ──
export function trackFeatureUse(feature: string) {
  gtag('event', 'feature_use', { feature, send_to: GA_ID });
}

// ── County request ──
export function trackCountyRequest(state: string) {
  gtag('event', 'county_request', { state, send_to: GA_ID });
}

// ── Scroll depth tracking ──
export function initScrollTracking() {
  if (typeof window === 'undefined') return;
  const depths = [25, 50, 75, 90];
  const fired = new Set<number>();

  const check = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
    );
    for (const d of depths) {
      if (scrollPercent >= d && !fired.has(d)) {
        fired.add(d);
        gtag('event', 'scroll_depth', { percent: d, send_to: GA_ID });
      }
    }
  };

  window.addEventListener('scroll', check, { passive: true });
}
